import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
} from '@mui/material';
import { Add, Edit, Delete, Star } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const HotelStarPricingTab = ({ data, onRefresh, onSuccess, onError }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    starRating: 1,
    pricePerNight: 0,
    description: '',
    isActive: true,
  });

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        starRating: item.starRating,
        pricePerNight: item.pricePerNight,
        description: item.description || '',
        isActive: item.isActive,
      });
    } else {
      setEditingItem(null);
      setFormData({
        starRating: 1,
        pricePerNight: 0,
        description: '',
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(
          `${API_BASE_URL}/pricing/hotel-stars/${editingItem.id}`,
          formData
        );
        onSuccess('Hotel star pricing updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/pricing/hotel-stars`, formData);
        onSuccess('Hotel star pricing created successfully');
      }
      handleCloseDialog();
      onRefresh();
    } catch (err) {
      onError('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this pricing tier?')) {
      try {
        await axios.delete(`${API_BASE_URL}/pricing/hotel-stars/${id}`);
        onSuccess('Hotel star pricing deleted successfully');
        onRefresh();
      } catch (err) {
        onError('Failed to delete: ' + err.message);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Hotel Star Rating Pricing</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Star Pricing
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Star Rating</TableCell>
              <TableCell>Price per Night</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {[...Array(item.starRating)].map((_, i) => (
                      <Star key={i} fontSize="small" sx={{ color: 'gold' }} />
                    ))}
                    <Typography>({item.starRating})</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" color="primary">
                    ${item.pricePerNight.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>{item.description}</TableCell>
                <TableCell>
                  <Chip
                    label={item.isActive ? 'Active' : 'Inactive'}
                    color={item.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(item)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Hotel Star Pricing' : 'Add Hotel Star Pricing'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Star Rating"
              type="number"
              value={formData.starRating}
              onChange={(e) =>
                setFormData({ ...formData, starRating: parseInt(e.target.value) })
              }
              inputProps={{ min: 1, max: 5 }}
              fullWidth
            />
            <TextField
              label="Price per Night ($)"
              type="number"
              value={formData.pricePerNight}
              onChange={(e) =>
                setFormData({ ...formData, pricePerNight: parseFloat(e.target.value) })
              }
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={2}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HotelStarPricingTab;
